import { useTranslate } from '@translations/useTranslate';
import {
  CustomButton,
  CustomInputFeedback,
  FormContainer,
  ImportantIBSModal,
} from '@ui/components/atoms';
import { SubFormSection } from '@ui/components/molecules/SubFormSection';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import pinImage from '@assets/svg/location-pin-svgrepo-com.svg';
import { CONTROL_POSITION, GOOGLE_MAP_DEFAULT_CONFIG } from '@utils/constants';
import GoogleMapReact from 'google-map-react';
import { useGetGoogleMapsAddressByLocation } from '@services/GoogleAPIAddress';
import TermsOfUse from '@ui/components/atoms/TermsOfUse';
import { SuccessMessage } from '../SuccessMessage';
import styles from './IBSRequest.module.scss';
import { useGetFormFields } from './hooks';

export const IBSRequestForm: FC = () => {
  const { t } = useTranslate();
  const recaptchaRef = React.useRef<ReCAPTCHA | undefined>();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapsApi, setMapsApi] = useState<typeof google.maps | null>(null);
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);
  const [showImportant, setShowImportant] = useState(false);
  const { formik, userDetailsFormItems, error, isSuccess } = useGetFormFields();
  const { BOOT_STRAP_URL_KEYS, MAP_OPTIONS, DEFAULT_ZOOM } = GOOGLE_MAP_DEFAULT_CONFIG;
  const { mutateAsync: getAddress } = useGetGoogleMapsAddressByLocation();
  const mapOptions = useMemo(
    () => ({
      ...MAP_OPTIONS,
      zoomControlOptions: {
        position: CONTROL_POSITION.RIGHT_TOP,
      },
      fullscreenControlOptions: {
        position: CONTROL_POSITION.RIGHT_BOTTOM,
      },
      fullscreenControl: false,
    }),
    [MAP_OPTIONS]
  );

  const handleApiLoaded = ({ map, maps }: { map: google.maps.Map; maps: typeof google.maps }) => {
    setMap(map);
    setMapsApi(maps);
  };

  const handleClick = useCallback(
    async ({ lat, lng }: GoogleMapReact.ClickEventValue) => {
      if (marker) {
        marker.setPosition({ lat, lng });
      } else if (mapsApi && map) {
        const newMarker = new mapsApi.Marker({
          position: { lat, lng },
          map,
          title: 'Selected Location',
          icon: {
            url: pinImage,
            scaledSize: new mapsApi.Size(25, 25),
          },
        });
        setMarker(newMarker);
      }
      const resp = await getAddress({ lat, lng });
      const addressValue = `https://maps.google.com/?q=${lat},${lng}`;
      formik.setFieldValue('locationURL', addressValue);
      formik.setFieldValue('entityAddress', resp);
      formik.setFieldError('entityAddress', undefined);
    },
    [formik, getAddress, map, mapsApi, marker]
  );
  return (
    <>
      {!isSuccess ? (
        <div className={styles.requestFormWrapper}>
          <h2 className="w-100 text-center fw-bold pt-2 pb-3">{t('ibs.request.header')}</h2>
          <div className="container-fluid px-0">
            <div className={styles.IbsReqestImage}>
              <div className={styles.mapWrapper}>
                <GoogleMapReact
                  bootstrapURLKeys={BOOT_STRAP_URL_KEYS}
                  defaultCenter={{ lat: 23.8859, lng: 45.0792 }}
                  defaultZoom={DEFAULT_ZOOM}
                  yesIWantToUseGoogleMapApiInternals={true}
                  options={mapOptions}
                  onGoogleApiLoaded={handleApiLoaded}
                  onClick={handleClick}
                ></GoogleMapReact>
              </div>
            </div>
            <div className="w-100 py-3 d-flex justify-content-center">
              <h5
                style={{ cursor: 'pointer', textDecoration: 'underline', color: '#d9534f' }}
                onClick={() => setShowImportant(true)}
              >
                {t('ibs.important.read')}
              </h5>
            </div>
            <div className={styles.formWrapper}>
              <FormContainer error={error} isIbsRequestForm={true}>
                <SubFormSection items={userDetailsFormItems} isSmallLabel={true} />
                <Form.Group className="w-100 pb-3 px-3 d-flex flex-column justify-content-start align-items-start">
                  <Form.Control
                    as="div"
                    className="border-0 w-auto p-0"
                    isInvalid={!!formik.errors.recaptchaToken && formik.touched.recaptchaToken}
                  >
                    <ReCAPTCHA
                      ref={recaptchaRef as React.LegacyRef<ReCAPTCHA>}
                      sitekey="6LdRwy4qAAAAAIN7oQMsZevTuDmW2m302maO2hwf"
                      onChange={(token) => {
                        formik.setFieldValue('recaptchaToken', token ?? '');
                        formik.validateField('recaptchaToken');
                      }}
                      onExpired={() => {
                        formik.setFieldValue('recaptchaToken', '');
                      }}
                    />
                  </Form.Control>

                  {formik.errors.recaptchaToken && (
                    <CustomInputFeedback errorFeedback={formik.errors.recaptchaToken} />
                  )}
                </Form.Group>

                <TermsOfUse />

                <Form.Group className="px-3">
                  <Form.Control as="div" className="border-0 w-auto p-0 d-flex gap-2">
                    <Form.Check
                      type="checkbox"
                      onChange={() => {
                        formik.setFieldValue('termsOfUse', !formik.values.termsOfUse);
                        formik.setFieldError('termsOfUse', undefined);
                      }}
                      required={true}
                      name="termsOfUse"
                    />
                    <Form.Label htmlFor="termsOfUse">{t('request.termsOfUse.label')}</Form.Label>
                  </Form.Control>
                  {formik.errors.termsOfUse && (
                    <CustomInputFeedback errorFeedback={formik.errors.termsOfUse} />
                  )}
                </Form.Group>

                <Form.Group className="px-3">
                  <Form.Control as="div" className="border-0 w-auto p-0 d-flex gap-2">
                    <Form.Check
                      type="checkbox"
                      name="privacyNotice"
                      required={true}
                      onChange={() => {
                        formik.setFieldValue('privacyNotice', !formik.values.privacyNotice);
                        formik.setFieldError('privacyNotice', undefined);
                      }}
                    />
                    <Form.Label htmlFor="privacyNotice">
                      {t('request.privacyNotice.label')}{' '}
                      <a href={t('request.privacyNotice.link')} target="_blank" rel="noreferrer">
                        {t('request.privacyNotice')}
                      </a>
                    </Form.Label>
                  </Form.Control>
                  {formik.errors.privacyNotice && (
                    <CustomInputFeedback errorFeedback={formik.errors.privacyNotice} />
                  )}
                </Form.Group>
                <Form.Group className="px-3">
                  <Form.Control as="div" className="border-0 w-auto p-0 d-flex gap-2">
                    <Form.Check
                      type="checkbox"
                      name="cstConditions"
                      required={true}
                      onChange={() => {
                        formik.setFieldValue('cstConditions', !formik.values.cstConditions);
                        formik.setFieldError('cstConditions', undefined);
                      }}
                    />
                    <Form.Label htmlFor="cstConditions">
                      {t('request.cstConditions.label')}
                    </Form.Label>
                  </Form.Control>
                  {formik.errors.cstConditions && (
                    <CustomInputFeedback errorFeedback={formik.errors.cstConditions} />
                  )}
                </Form.Group>

                <div className="w-100 pt-3 pb-5 d-flex justify-content-center align-items-center">
                  <CustomButton
                    variant="primary"
                    text={t('submit')}
                    onClick={() => {
                      formik.handleSubmit();
                    }}
                  />
                </div>
              </FormContainer>
            </div>
          </div>
          <ImportantIBSModal show={showImportant} onClose={() => setShowImportant(false)} t={t} />
        </div>
      ) : (
        <SuccessMessage isOwn />
      )}
    </>
  );
};
